/* eslint-disable eqeqeq */
import $ from "jquery";
import { I18n } from "react-redux-i18n";
import auth, { get, post, getLocale } from "../auth";
import orderListUtil, { getTable, getTableName, getTableOccupation, updateTableOccupations, isTakeawayTable } from "../order-list-util";
import { getMenuItem } from "../admin";
import { selectTableOccupation, showEditTable, selectNewOrders } from "../tables-overview";

// Table actions of the voice assistant: select / open (lock), close (unlock), move, join, takeaway, person count, read the orders.
// They use the same server calls and the same client data as the tables overview. What cannot be undone easily (closing a table with
// orders, moving it, adding a table) is not executed here: it is handed to confirm(), which shows it to the waiter as a button.

const MAX_TABLES_IN_CONTEXT_CHARS = 6000;
const MAX_LISTED_TABLES = 80;
const MAX_ORDER_LINES = 40;
const MAX_PERSON_DIFF = 30;
const APPLY_TIMEOUT_MS = 8000;

const t = (key, params) => I18n.t("admin_local." + key, { defaultValue: key, ...params });

const norm = s =>
  String(s == null ? "" : s)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

const tableList = () => (auth.myStatus && auth.myStatus.restaurant_tables) || [];
const tableLabel = number => String(getTableName(number));
const isSpecial = number => {
  try {
    return !!isTakeawayTable(number);
  } catch (e) {
    return false;
  }
};

// ---------------------------------------------------------------- what is on the tables

const tablesOf = occupation => (occupation.restaurantTables || []).filter(x => x.active !== false && x.restaurantTable).map(x => Number(x.restaurantTable.number));
const isBooked = occupation => occupation.occupationStart == null && !!occupation.bookedOccupationStart;
const occupationsOf = number => (orderListUtil.tableOccupations || []).filter(o => o.active !== false && !isBooked(o) && tablesOf(o).includes(Number(number)));
const ordersOf = occupation =>
  (orderListUtil.ordersCache || []).filter(o => o.tableOccupationId == occupation.id && !o.parentOrderId && o.state !== "cancelled" && o.recordState !== "DELETED");
const brief = occupation => ({
  occupation_id: occupation.id,
  persons: occupation.personCount,
  orders: ordersOf(occupation).length,
  takeaway: occupation.takeaway ? true : undefined,
  selected: occupation.id == sessionStorage.tableOccupationSelected ? true : undefined
});

const currentSelection = () => {
  const id = sessionStorage.tableOccupationSelected;
  const occupation = id && id !== "null" && id !== "undefined" ? getTableOccupation(id) : null;
  return occupation ? { occupation, number: Number(sessionStorage.tableNumberSelected) || tablesOf(occupation)[0] } : null;
};

export const tablesContext = () => {
  const lines = [];
  let length = 0;
  tableList().forEach(tb => {
    if (isSpecial(tb.number)) return;
    const line = tb.number + ": " + tableLabel(tb.number) + (tb.seats ? " (" + tb.seats + " seats)" : "") + (tb.shareable ? " shareable" : "");
    if (length + line.length + 1 > MAX_TABLES_IN_CONTEXT_CHARS) return;
    length += line.length + 1;
    lines.push(line);
  });
  return lines.length ? "TABLES of the restaurant (number: name). Table numbers are what the waiter says:\n" + lines.join("\n") : "";
};

// ---------------------------------------------------------------- resolving what the waiter said

const resolveTable = query => {
  if (query == null || String(query).trim() === "") return { error: "table is missing" };
  const all = tableList().filter(tb => !isSpecial(tb.number));
  const text = String(query).trim();
  const s = norm(text);
  let found = all.filter(tb => String(tb.number) === text);
  if (!found.length) found = all.filter(tb => tb.name && norm(tb.name) === s);
  if (!found.length) {
    const digits = s.match(/\d+/);
    if (digits) found = all.filter(tb => String(tb.number) === digits[0]);
  }
  if (!found.length && s.length >= 2) found = all.filter(tb => tb.name && norm(tb.name).includes(s));
  if (!found.length) return { error: "unknown table '" + text + "', use list_tables" };
  if (found.length > 1) return { error: "table '" + text + "' is ambiguous: " + found.map(tb => tb.number + " " + tableLabel(tb.number)).join(", ") };
  return { table: found[0] };
};

// The table the waiter means: the named one or, without a name, the one that is selected on the screen.
const resolveOccupation = ({ table, occupation_id }) => {
  if (table == null || String(table).trim() === "") {
    const selected = currentSelection();
    if (!selected) return { error: "no table is selected, say which table" };
    return { number: selected.number, occupation: selected.occupation };
  }
  const r = resolveTable(table);
  if (r.error) return r;
  const number = Number(r.table.number);
  const occupations = occupationsOf(number);
  if (occupation_id != null) {
    const occupation = occupations.find(o => o.id == occupation_id);
    return occupation ? { number, occupation, tableInfo: r.table } : { error: "no such occupation on that table" };
  }
  if (occupations.length > 1) return { error: "several parties sit at table " + tableLabel(number) + ", ask which one", occupations: occupations.map(brief) };
  return { number, occupation: occupations[0] || null, tableInfo: r.table };
};

// ---------------------------------------------------------------- server calls

const failureText = xhr => {
  const status = xhr && xhr.status;
  if (status === 403) return "the waiter is not allowed to do this";
  if (status === 400) return "the server refused it (the table may not be free or not shareable)";
  return "the server did not answer";
};

const errorText = error => {
  try {
    return I18n.t(error, { defaultValue: error });
  } catch (e) {
    return String(error);
  }
};

const call = (path, post_) =>
  new Promise(resolve => {
    const url = "adminService/" + localStorage.restaurantSelected + "/" + path;
    // post_: the call changes data and is a POST (no body needed)
    (post_ ? post(url, [], undefined, false, 0, true, 30000) : get(url, undefined, undefined, 30000))
      .done(data => {
        let result = data;
        if (typeof result === "string") {
          try {
            result = JSON.parse(result);
          } catch (e) {
            // not JSON, nothing to read from it
          }
        }
        if (result && result.error) resolve({ error: errorText(result.error) });
        else resolve({ data: result });
      })
      .fail(xhr => {
        auth.ajaxError = false;
        resolve({ error: failureText(xhr) });
      });
  });

const occupationsFrom = data => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (data.tableOccupations) return data.tableOccupations;
  return data.tableOccupation ? [data.tableOccupation] : [];
};

// Feeds the answer of the server into the same client state the tables overview keeps, so the screen follows.
const apply = async data => {
  const list = occupationsFrom(data);
  if (!list.length) return;
  if (!orderListUtil.tableOccupations) orderListUtil.tableOccupations = [];
  const timeout = new Promise(resolve => setTimeout(resolve, APPLY_TIMEOUT_MS));
  try {
    await Promise.race([new Promise(resolve => updateTableOccupations(list, (data && data.orders) || []).always(resolve)), timeout]);
  } catch (e) {
    console.error("voice table update failed", e);
  }
};

// orderMenuItem silently does nothing for an occupation the client does not know, so a just opened table must be in the list.
const ensureKnown = occupation => {
  if (!orderListUtil.tableOccupations) orderListUtil.tableOccupations = [];
  if (getTableOccupation(occupation.id)) return;
  occupation.restaurantTables = (occupation.restaurantTables || []).filter(x => x.active);
  orderListUtil.tableOccupations.push(occupation);
};

const select = (occupationId, number) => {
  sessionStorage.tableNumberSelected = number;
  try {
    selectTableOccupation(occupationId);
  } catch (e) {
    sessionStorage.tableOccupationSelected = occupationId;
  }
};

// The waiter follows the assistant on the screen: a table that was selected by voice from the tables overview opens like after a tap on it.
const tableViewShown = () => $("#editTable").length > 0 && !$("#editTable").hasClass("hidden");

const showTableOnScreen = () => {
  try {
    if (!$("#editTable").length || tableViewShown() || !currentSelection()) return false;
    showEditTable();
    orderListUtil.processOrders(orderListUtil.ordersCache);
    console.log("voice order opened table on screen:", sessionStorage.tableNumberSelected);
    return true;
  } catch (e) {
    console.error("voice order: could not open the table on the screen", e);
    return false;
  }
};

// The menu with its categories only exists in the new orders of an open table. The flag selectNewOrders can be left over from an earlier
// table, so what the screen shows decides: the table view must be open and its right pane (the menu) visible.
export const newOrdersOnScreen = () =>
  tableViewShown() && $("#right_pane").length > 0 && !$("#right_pane").hasClass("hidden") && localStorage.selectNewOrders === "true";

export const showNewOrdersOnScreen = () => {
  try {
    if (!currentSelection()) {
      console.info("voice order: no known table selected, new orders not opened");
      return false;
    }
    const opened = showTableOnScreen();
    if (!tableViewShown()) {
      console.info("voice order: no table view on the screen, new orders not opened; editTable", $("#editTable").length, "floorplan", $("#main-floorplan").length);
      return false;
    }
    if (!opened && newOrdersOnScreen()) return true;
    console.log("voice order: not in the new orders (right pane", $("#right_pane").hasClass("hidden") ? "hidden" : "shown", "flag", localStorage.selectNewOrders + "), going in");
    selectNewOrders(true);
    console.log("voice order went into new orders:", sessionStorage.tableNumberSelected);
    return localStorage.selectNewOrders === "true";
  } catch (e) {
    console.error("voice order: could not go into the new orders", e);
    return false;
  }
};

const clearSelectionIf = occupationId => {
  if (sessionStorage.tableOccupationSelected == occupationId) {
    sessionStorage.tableOccupationSelected = "";
    sessionStorage.tableNumberSelected = "";
  }
};

// ---------------------------------------------------------------- tools

const listTables = ({ status }) => {
  const wanted = status === "free" || status === "occupied" ? status : "all";
  const rows = [];
  tableList().forEach(tb => {
    if (isSpecial(tb.number)) return;
    const occupations = occupationsOf(tb.number);
    const state = occupations.length ? "occupied" : "free";
    if (wanted !== "all" && wanted !== state) return;
    rows.push({ table: tb.number, name: tableLabel(tb.number), seats: tb.seats || undefined, shareable: tb.shareable ? true : undefined, status: state, occupations: occupations.length ? occupations.map(brief) : undefined });
  });
  const selected = currentSelection();
  return { selected_table: selected ? tableLabel(selected.number) : null, tables: rows.slice(0, MAX_LISTED_TABLES), truncated: rows.length > MAX_LISTED_TABLES ? true : undefined };
};

const selectTable = ({ table, seats, occupation_id, new_occupation }) => {
  const r = resolveTable(table);
  if (r.error) return r;
  return selectResolved(r, { seats, occupation_id, new_occupation });
};

// The table that is selected on the screen but has no party yet (nothing locked it). Locks it so orders can be added.
export const openSelectedTable = async () => {
  const number = Number(sessionStorage.tableNumberSelected);
  const tb = number ? getTable(number) : null;
  if (!tb) return { error: "no table" };
  const result = await selectResolved({ table: tb }, { seats: 1 });
  return result.error ? result : { ok: true };
};

async function selectResolved(r, { seats, occupation_id, new_occupation }) {
  const number = Number(r.table.number);
  const occupations = occupationsOf(number);
  let occupation = null;
  if (occupation_id != null) {
    occupation = occupations.find(o => o.id == occupation_id);
    if (!occupation) return { error: "no such occupation on that table" };
  } else if (!new_occupation) {
    if (occupations.length === 1) occupation = occupations[0];
    else if (occupations.length > 1) {
      // a point of sale table opens a new party for every sale; an empty one is simply used again
      occupation = r.table.pos ? occupations.find(o => ordersOf(o).length === 0) || null : null;
      if (!occupation) return { error: "several parties sit at table " + tableLabel(number) + ", ask which one or if it is a new party", occupations: occupations.map(brief) };
    }
  }
  let created = false;
  if (!occupation) {
    if (occupations.length && !r.table.shareable && !r.table.pos) return { error: "table " + tableLabel(number) + " is occupied and cannot be shared" };
    const persons = Math.max(1, Math.min(Number(seats) || 1, 99));
    const result = await call("lockTable/" + number + "?seats=" + persons + "&lang=" + encodeURIComponent(localStorage.language || "en") + "&customerId=");
    if (result.error) return { error: result.error };
    const list = occupationsFrom(result.data);
    if (!list.length) return { error: "the table could not be opened" };
    await apply(result.data);
    occupation = list[0];
    ensureKnown(occupation);
    created = true;
  }
  select(occupation.id, number);
  showTableOnScreen();
  return { table: tableLabel(number), occupation_id: occupation.id, opened_new: created || undefined, persons: occupation.personCount, orders: ordersOf(occupation).length };
}

const unlockTable = async (args, ctx) => {
  const r = resolveOccupation(args);
  if (r.error) return r;
  if (!r.occupation) return { error: "table " + tableLabel(r.number) + " is free, there is nothing to close" };
  const { occupation, number } = r;
  const orders = ordersOf(occupation);
  const unpaid = orders.filter(o => !o.paid).length;
  const run = async () => {
    const result = await call("" + occupation.id + "/unlockTable/" + number + "?cancelAll=" + (unpaid > 0) + "&lang=" + encodeURIComponent(localStorage.language || "en"));
    if (result.error) return { error: result.error };
    clearSelectionIf(occupation.id);
    await apply(result.data);
    return { text: "table " + tableLabel(number) + " closed" };
  };
  if (orders.length === 0) {
    const done = await run();
    return done.error ? { error: done.error } : { done: done.text };
  }
  const label = tableLabel(number);
  return ctx.confirm(unpaid > 0 ? t("voice_unlock_confirm_cancel", { table: label, count: unpaid }) : t("voice_unlock_confirm", { table: label }), run);
};

const moveTable = (args, ctx) => {
  const r = resolveOccupation({ table: args.from_table, occupation_id: args.occupation_id });
  if (r.error) return r;
  if (!r.occupation) return { error: "table " + tableLabel(r.number) + " is free, there is nothing to move" };
  const target = resolveTable(args.to_table);
  if (target.error) return target;
  const to = Number(target.table.number);
  const from = r.number;
  if (to === from) return { error: "the target is the same table" };
  const occupation = r.occupation;
  const targets = occupationsOf(to);
  const fromLabel = tableLabel(from);
  const toLabel = tableLabel(to);
  let path;
  let text;
  const selectNumber = to;
  let selectOccupation = occupation.id;
  if (targets.length === 0) {
    path = "changeToTable/" + occupation.id + "/" + from + "/" + to;
    text = t("voice_move_confirm", { from: fromLabel, to: toLabel });
  } else {
    let targetOccupation = null;
    if (args.target_occupation_id != null) targetOccupation = targets.find(o => o.id == args.target_occupation_id);
    else if (targets.length === 1) targetOccupation = targets[0];
    const mode = args.mode;
    if (target.table.shareable && !["join", "new", "extend"].includes(mode))
      return { error: "table " + toLabel + " is shared and has guests, ask the waiter: mode 'join' (merge with those guests), 'new' (separate party) or 'extend' (same party, more tables)", target_occupations: targets.map(brief) };
    if (mode === "new" && target.table.shareable) {
      path = "createNewTableOccupationForMate/" + occupation.id + "/" + from + "/-1/" + to + "?allPaidMates=true";
      text = t("voice_new_confirm", { from: fromLabel, to: toLabel });
    } else if (mode === "extend" && target.table.shareable) {
      path = "changeTableForMate/" + occupation.id + "/" + from + "/-1/" + to + "?allPaidMates=true";
      text = t("voice_extend_confirm", { from: fromLabel, to: toLabel });
    } else {
      if (!targetOccupation) return { error: "several parties sit at table " + toLabel + ", ask which one", target_occupations: targets.map(brief) };
      path = "changeTableOccupationForMate/" + occupation.id + "/" + from + "/-1/" + targetOccupation.id + "/" + to + "?allPaidMates=true";
      text = t("voice_join_confirm", { from: fromLabel, to: toLabel });
      selectOccupation = targetOccupation.id;
    }
  }
  const run = async () => {
    const result = await call(path);
    if (result.error) return { error: result.error };
    await apply(result.data);
    const stillThere = getTableOccupation(selectOccupation);
    if (stillThere) select(selectOccupation, selectNumber);
    else clearSelectionIf(occupation.id);
    return { text: "table " + fromLabel + " moved to table " + toLabel };
  };
  return ctx.confirm(text, run);
};

const addTable = (args, ctx) => {
  const r = resolveOccupation({ table: args.table, occupation_id: args.occupation_id });
  if (r.error) return r;
  if (!r.occupation) return { error: "table " + tableLabel(r.number) + " is free, open it first" };
  const target = resolveTable(args.additional_table);
  if (target.error) return target;
  const to = Number(target.table.number);
  if (tablesOf(r.occupation).includes(to)) return { error: "that table is already part of it" };
  if (occupationsOf(to).length && !target.table.shareable) return { error: "table " + tableLabel(to) + " is occupied" };
  const occupation = r.occupation;
  const run = async () => {
    const result = await call("addAdditionalTable/" + occupation.id + "/" + to);
    if (result.error) return { error: result.error };
    await apply(result.data);
    return { text: "table " + tableLabel(to) + " added to table " + tableLabel(r.number) };
  };
  return ctx.confirm(t("voice_add_table_confirm", { from: tableLabel(r.number), to: tableLabel(to) }), run);
};

const setTakeaway = async args => {
  const r = resolveOccupation(args);
  if (r.error) return r;
  if (!r.occupation) return { error: "table " + tableLabel(r.number) + " is free, open it first" };
  const settings = auth.myStatus && auth.myStatus.restaurant_settings && auth.myStatus.restaurant_settings.takeaway;
  if (!settings || !settings.enabled) return { error: "takeaway is not enabled in this restaurant" };
  const takeaway = args.takeaway !== false;
  const result = await call("setTakeAway/" + r.occupation.id + "/" + takeaway);
  if (result.error) return { error: result.error };
  await apply(result.data);
  return { table: tableLabel(r.number), takeaway };
};

const setPersonCount = async args => {
  const r = resolveOccupation(args);
  if (r.error) return r;
  if (!r.occupation) return { error: "table " + tableLabel(r.number) + " is free, open it first" };
  const wanted = Math.round(Number(args.persons));
  if (!(wanted >= 1 && wanted <= 99)) return { error: "persons must be between 1 and 99" };
  let current = Number(r.occupation.personCount) || 0;
  if (Math.abs(wanted - current) > MAX_PERSON_DIFF) return { error: "that is too big a change, ask the waiter again" };
  let last = null;
  while (current !== wanted) {
    const result = await call((current < wanted ? "incrementPersonCount/" : "decrementPersonCount/") + r.occupation.id);
    if (result.error) return { error: result.error };
    last = result.data;
    current += current < wanted ? 1 : -1;
  }
  if (last) await apply(last);
  return { table: tableLabel(r.number), persons: wanted };
};

const getTableOrders = args => {
  const r = resolveOccupation(args);
  if (r.error) return r;
  if (!r.occupation) return { table: tableLabel(r.number), status: "free" };
  const rows = new Map();
  ordersOf(r.occupation).forEach(o => {
    const menuItem = (o.menuItem && getMenuItem(o.menuItem.id)) || o.menuItem;
    const name = (menuItem && getLocale(menuItem.name)) || (o.menuItem && o.menuItem.id) || "?";
    const key = name + "|" + o.state + "|" + !!o.paid;
    const row = rows.get(key) || { item: name, menu_item_id: o.menuItem ? o.menuItem.id : undefined, state: o.state, paid: !!o.paid, count: 0 };
    row.count++;
    rows.set(key, row);
  });
  const list = Array.from(rows.values());
  return { table: tableLabel(r.number), persons: r.occupation.personCount, takeaway: r.occupation.takeaway ? true : undefined, orders: list.slice(0, MAX_ORDER_LINES), truncated: list.length > MAX_ORDER_LINES ? true : undefined };
};

const TOOLS = {
  list_tables: listTables,
  select_table: selectTable,
  unlock_table: unlockTable,
  move_table: moveTable,
  add_table: addTable,
  set_takeaway: setTakeaway,
  set_person_count: setPersonCount,
  get_table_orders: getTableOrders
};

export const isTableTool = name => Object.prototype.hasOwnProperty.call(TOOLS, name);

// ctx.confirm(text, run) hands an action to the waiter and returns what the model is told meanwhile.
export const runTableTool = async (name, args, ctx) => {
  try {
    return await TOOLS[name](args || {}, ctx);
  } catch (e) {
    console.error("voice table tool failed", name, e);
    return { error: "tool failed" };
  }
};
